import { supabase } from './supabaseClient.js';
import { splitAudibility } from './director.js';

// Bug #11 fix: Sanitize names for Supabase .or() filter syntax.
// Parentheses, commas, and quotes break PostgREST filter parsing.
function sanitizeForFilter(name) {
  return name.replace(/[(),."'\\]/g, '');
}

/**
 * Retrieves character-specific Discord RP history snippets to use as context
 * for AI character responses — the "EXAMPLE WRITING, match this voice" block.
 *
 * Two retrieval modes:
 *   • SEMANTIC (preferred) — when `sceneEmbedding` is passed, calls the
 *     match_character_voice RPC: of EVERY passage in this character's whole
 *     Discord history, return the ones most relevant to what's happening in
 *     the current scene. So a tense confrontation pulls the character's tense
 *     confrontations, not 8 random romantic snippets. This is what makes the
 *     character actually feel like themselves *for this moment*.
 *     WRITER BIASING: before the RPC call, we look up the primary writer
 *     (haley/nicole) for this character via get_character_primary_writer.
 *     match_character_voice then scopes samples to that writer only — so
 *     Cruz sounds like Haley's Cruz (since she plays him on Discord), not
 *     an average of every writer who ever borrowed his name. NULL writer
 *     (no clear mapping) falls back to all non-excluded writers.
 *   • RANDOM (fallback) — original behavior: a shuffled sample. Used when no
 *     embedding is available, or while rp_history is still mid-backfill and
 *     the semantic query returns nothing.
 *
 * STORY ISOLATION (May 20 fix): The RPC's default behavior now EXCLUDES
 * `source='app'` rows — which means cross-story bleed (Cruz's voice from
 * Haley's kidnap scene surfacing inside Nicole's cafe scene) is impossible
 * by default. Pass `currentStoryId` (and optionally `linkedStoryIds`) when
 * calling FROM an active app story to re-include that story's own mirrored
 * messages — those count as in-scene context. For DMs and channels, pass
 * nothing: voice samples become Discord-only, which is the intended
 * cross-platform reference behavior.
 *
 * @param {string} characterName - The character's name (e.g. "Cruz", "Kya")
 * @param {number} limit - Max snippets to return (default 8)
 * @param {number[]|null} sceneEmbedding - optional 768-dim embedding of the
 *        current scene beat. Pass it to get semantic retrieval. Internally
 *        pre-stringified to '[v1,v2,...]' before sending to
 *        match_character_voice — PostgREST's implicit JSON-array → halfvec
 *        coercion path doesn't use the HNSW index and statement-timeouts on
 *        the 35k-row rp_history corpus. The text-literal path goes through
 *        pgvector's normal input parser → HNSW index used.
 * @param {string|null} currentStoryId - optional. If calling from inside an
 *        app story (SixGuysRP), pass the story id. Otherwise pass null.
 * @param {string[]|null} linkedStoryIds - optional. Stories the user has
 *        explicitly linked to the current one (via conversation_links). The
 *        RPC will also include app rows from these stories.
 * @returns {Promise<Array<{channel:string, text:string}>>}
 */

// Channels that must never supply a VOICE SAMPLE, even though the roleplay in
// them is real and played. Haley, 2026-08-02: "don't use yandere cruz for it
// use other cruz routes as voice sample" — a deliberately obsessive AU is the
// wrong thing to imitate in an ordinary scene. Data, not a hardcoded name, so
// the list is hers to extend without a deploy.
let _voiceExclusions = null;
async function loadVoiceChannelExclusions() {
  if (_voiceExclusions) return _voiceExclusions;
  try {
    const { data } = await supabase.from('voice_channel_exclusions').select('channel_name');
    _voiceExclusions = new Set((data || []).map(r => r.channel_name));
  } catch {
    _voiceExclusions = new Set();   // never block a reply on this
  }
  return _voiceExclusions;
}

// ─── THE 50-SECOND TURN (2026-09-16) ─────────────────────────────────────────
// Haley: "typing takes a lot longer than it used to to go from typed to
// sent." Her Teagan route measured player message → reply at p50 50 s (was
// 15 s in August) with the model call itself at ~6 s. Four of those seconds,
// FOUR TIMES PER TURN, were this file's ILIKE fallback for supporting cast
// with no Discord history at all (Mateo, Liz, Lander, Minerva): the rp_history
// read policy ran a role function per row and blocked the trigram indexes,
// so every miss was a 4.3 s sequential scan — repeated on every turn and
// again on every duplicate-reply retry.
//   • rp_history_name_samples() (migration 20260916193000) is the same ILIKE
//     behind a SECURITY DEFINER gate checked once, through the indexes:
//     4 names in ~30 ms.
//   • A name that came back empty from BOTH paths is remembered for the
//     session (NO_CORPUS_TTL_MS); an app-only character costs nothing after
//     the first look.
const NO_CORPUS_TTL_MS = 10 * 60 * 1000;
const noCorpusUntil = new Map(); // lower-cased name → epoch ms until which we skip the lookup
export function hasNoCorpus(name) {
  const until = noCorpusUntil.get(String(name || '').toLowerCase());
  return typeof until === 'number' && until > Date.now();
}
export function rememberNoCorpus(name) {
  if (name) noCorpusUntil.set(String(name).toLowerCase(), Date.now() + NO_CORPUS_TTL_MS);
}
export function forgetNoCorpus(name) {
  if (name) noCorpusUntil.delete(String(name).toLowerCase()); else noCorpusUntil.clear();
}

export async function getCharacterDiscordContext(characterName, limit = 8, sceneEmbedding = null, currentStoryId = null, linkedStoryIds = null) {
  if (!characterName || typeof characterName !== 'string') {
    return [];
  }
  if (hasNoCorpus(characterName)) return [];

  // ── SEMANTIC PATH: scene-relevant voice samples from the whole corpus ──
  if (Array.isArray(sceneEmbedding) && sceneEmbedding.length > 0) {
    try {
      // Resolve which player normally writes this character (haley/nicole)
      // via the writer_accounts mapping. When set, match_character_voice
      // biases samples toward that writer's writing of this character —
      // so Cruz (Haley plays him on Discord) sounds like Haley's Cruz, not
      // some mix of voices. Falls back to unscoped (all non-excluded
      // writers) if there's no clear mapping for this character.
      let primaryWriter = null;
      try {
        const { data: writerData } = await supabase.rpc('get_character_primary_writer', {
          p_character_id: characterName,
        });
        if (writerData && typeof writerData === 'string' && writerData.length > 0) {
          primaryWriter = writerData;
        }
      } catch (_) { /* non-fatal — drop to unscoped retrieval */ }

      const { data, error } = await supabase.rpc('match_character_voice', {
        p_character_name: characterName,
        // halfvec(768) param — must be sent as a pgvector text literal, not
        // a raw number[]. See JSDoc above for why.
        p_query_embedding: '[' + sceneEmbedding.join(',') + ']',
        p_limit: limit,
        p_writer: primaryWriter, // null = any non-excluded writer; else scoped
        // STORY ISOLATION — see JSDoc. Both default to null which makes
        // the RPC return Discord-only samples (no app-mirrored bleed).
        p_current_story_id: currentStoryId ? String(currentStoryId) : null,
        p_linked_story_ids: Array.isArray(linkedStoryIds) && linkedStoryIds.length > 0
          ? linkedStoryIds.map(String)
          : null,
      });
      if (!error && data && data.length > 0) {
        return data.map(r => ({
          channel: r.channel_name || 'Unknown',
          text: (r.content || '').substring(0, 600),
        }));
      }
      // error OR empty (rp_history not embedded yet) → fall through to random
    } catch (err) {
      console.warn('[discordContext] semantic voice match failed, falling back to random:', err?.message || err);
    }
  }

  // ── WRITER-SCOPED / RANDOM FALLBACK ─────────────────────────────────────
  // Bias toward the player who actually writes this character (Haley/Nicole),
  // so the voice samples are THEIR version of the character (how *we've* written
  // them), not a random other writer's. Falls back to a loose name match if
  // there's no clear writer/account mapping for this character.
  try {
    let scopedAccounts = null;
    try {
      const { data: pw } = await supabase.rpc('get_character_primary_writer', { p_character_id: characterName });
      if (pw && typeof pw === 'string' && pw.length > 0) {
        const { data: accts } = await supabase.from('writer_accounts').select('account_name').eq('writer', pw);
        const cn = String(characterName).toLowerCase();
        const names = (accts || []).map(a => a.account_name).filter(n => String(n).toLowerCase().includes(cn));
        if (names.length) scopedAccounts = names;
      }
    } catch (_) { /* non-fatal — drop to loose match */ }

    // Big pool for sampling. The name form goes through the indexed RPC (see
    // THE 50-SECOND TURN above); the writer-scoped form is an exact author
    // match, which the btree on author already serves.
    const { data, error } = scopedAccounts
      ? await supabase.from('rp_history').select('channel_name, content, author').gt('content', '').in('author', scopedAccounts).limit(limit * 6)
      : await supabase.rpc('rp_history_name_samples', { p_names: [characterName], p_limit: limit * 6 });

    if (error) {
      console.error('Discord context fetch error:', error);
      return [];
    }
    if (!data || data.length === 0) { rememberNoCorpus(characterName); return []; }

    // THE SEMANTIC PATH IS NOT THE ONLY PATH. match_character_voice enforces
    // voice_channel_exclusions in SQL, but this fallback runs whenever the
    // scene embedding is null and queries rp_history directly — so without
    // this, excluding yandere-cruz would have worked right up until the
    // embedding failed, and then silently stopped. Fixing one path and
    // missing its twin is how most of the bugs in this pipeline shipped.
    const excluded = await loadVoiceChannelExclusions();

    // Filter for substantial messages and shuffle for variety
    const substantial = data.filter(msg =>
      msg.content && msg.content.length > 50 && !excluded.has(msg.channel_name));
    // Deterministic stride-sample (audit P3-9): the shuffle made every
    // regeneration non-reproducible. A stride over the recency-ordered
    // pool keeps the variety without the randomness.
    {
      const stride = Math.max(1, Math.floor(substantial.length / Math.max(1, Math.min(substantial.length, 24))));
      const strided = substantial.filter((_, i) => i % stride === 0);
      substantial.length = 0;
      substantial.push(...strided);
    }
    return substantial
      .slice(0, limit)
      .map(msg => ({
        channel: msg.channel_name || 'Unknown',
        text: msg.content.substring(0, 600), // longer excerpts capture full voice + paragraph patterns
      }));
  } catch (err) {
    console.error('Error fetching character Discord context:', err);
    return [];
  }
}

/**
 * Retrieves the LAST N messages this character actually said in the current
 * platform — across DM threads with this user and channel messages where
 * this character is the AI sender. This is different from
 * getCharacterDiscordContext (which is a random sample of historical Discord
 * RP) — this is recency-ordered and lets the model see exactly what its
 * character has been saying lately, so the next reply continues without
 * tone whiplash.
 *
 * @param {string} characterId - Internal character id (e.g. "cruz", "apollo")
 * @param {string} userId - The current player's user id (scopes DM lookups
 *   to messages this character has sent to this user)
 * @param {number} limit - Max messages to return (default: 25)
 * @param {object} [opts]
 * @param {string} [opts.channelId] - Scope the CHANNEL half to one channel.
 *   Haley, 2026-08-02: "I just don't want other channels in the Our Channels
 *   page referenced for responses." Until this existed the channel query
 *   filtered on character_id and is_ai_character and NOTHING ELSE, so every
 *   reply Cruz had ever written in ANY channel was fed into his Pack House
 *   prompt as "what you have been saying lately" — other scenes, other
 *   partners, other tones. Omitting it keeps the old cross-channel behaviour,
 *   which is still right for the story surface.
 * @param {boolean} [opts.includeDMs=true] - Include this character's DMs with
 *   the player. A channel scene is not a DM thread; ChannelView turns it off.
 * @returns {Promise<Array<{source:'dm'|'channel', content:string, created_at:string}>>}
 */
export async function getCharacterRecentMessages(characterId, userId, limit = 25, opts = {}) {
  const { channelId = null, includeDMs = true, sceneTag = null, conversationId = null } = opts;
  if (!characterId) return [];

  // Pull from both sources, then merge by recency. Each query asks for `limit`
  // rows so worst case we have 2*limit candidates to pick from.
  const POOL = limit * 2;

  const promises = [];

  // DM messages by this character — THIS CONVERSATION ONLY.
  //
  // Nicole, 2026-10-06: "the DM memory is pulling from old scenes again".
  // This branch pooled the character's last lines from EVERY thread the writer
  // has with them, so a live Ford thread's RECENT VOICE carried his lines from
  // a route she left in June (and the canned daily starters that kept landing
  // there). The framing below says "not events in THIS scene", and the model
  // still played them as history. The same rule the channel branch adopted on
  // 2026-08-10 applies: a thread already carries its own complete history;
  // borrowing another thread's lines adds nothing but leakage. A caller with
  // no conversation (a story) gets no DM lines at all. Canned daily starters
  // (metadata.daily_starter) were never the character speaking and are
  // excluded everywhere.
  if (userId && includeDMs && conversationId) {
    promises.push(
      supabase
        .from('dm_messages')
        .select('content, created_at, conversation_id, sender_type, metadata, dm_conversations!inner(character_id, user_id)')
        .eq('sender_type', 'character')
        .eq('conversation_id', conversationId)
        .eq('dm_conversations.character_id', characterId)
        .eq('dm_conversations.user_id', userId)
        .is('metadata->daily_starter', null)
        .gt('content', '')
        .order('created_at', { ascending: false })
        .limit(POOL)
        .then(r => (r.data || []).map(m => ({ source: 'dm', content: m.content, created_at: m.created_at })))
        .catch(() => [])
    );
  }

  // ── CHANNEL MESSAGES: SAME CHANNEL, SAME WRITER, OR NOT AT ALL ───────────
  //
  // Haley, 2026-08-10: "a response from cruz in the co-op bled into the rp
  // section for one of nicole's stories and the response was almost word for
  // word."
  //
  // This branch had NO user filter and only an OPTIONAL channel filter — the
  // comment below literally said "THE SCOPE THAT WAS MISSING. Without it this
  // reads every channel" and then applied it conditionally. The DM branch above
  // filters by dm_conversations.user_id; this one filtered by neither user nor
  // surface.
  //
  // Measured: of the three callers, only ChannelView passes a scope. DMView and
  // SixGuysRP pass none, so a DM or a STORY asking "what has Cruz said lately"
  // received his lines from every channel and BOTH writers — which is exactly a
  // co-op reply arriving in someone else's story, close enough to be recognised
  // word for word.
  //
  // Two rules now, and the first is the important one:
  //   • no channel scope → no channel lines. A DM and a story already carry
  //     their own complete history (v244/v245); borrowing a third surface's
  //     lines adds nothing but leakage.
  //   • always scope to the WRITER. Haley's Cruz and Nicole's Cruz build up
  //     separately, which is the isolation Haley asked for when she suggested
  //     "different copies of the same character so character doesn't change
  //     much from interactions with one user vs the other".
  promises.push((() => {
    if (!channelId) return Promise.resolve([]);
    let q = supabase
      .from('channel_messages')
      .select('content, created_at')
      .eq('character_id', characterId)
      .eq('is_ai_character', true)
      .gt('content', '')
      .eq('channel_id', channelId);
    if (userId) q = q.eq('user_id', userId);
    // Audit P1-5 (scene wall): channel-scoped was not SCENE-scoped — in a
    // two-scene room the "last things you said" mixed both scenes. Shared
    // scenes stay visible; the other scene's lines do not.
    if (sceneTag) q = q.or(`scene_tag.eq.${sceneTag},scene_shared.eq.true,scene_tag.is.null`);
    return q
      .order('created_at', { ascending: false })
      .limit(POOL)
      .then(r => (r.data || []).map(m => ({ source: 'channel', content: m.content, created_at: m.created_at })))
      .catch(() => []);
  })());

  try {
    const results = await Promise.all(promises);
    const combined = results.flat();
    combined.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return combined.slice(0, limit);
  } catch (err) {
    console.error('[getCharacterRecentMessages] error:', err);
    return [];
  }
}

/**
 * Pull the user's prior corrections / regenerate-feedback for this character.
 * Sourced from dm_messages.feedback_context (set when the user regenerates a
 * reply with notes on what to do differently).
 *
 * Returned in recency order (newest first) so the most recent corrections
 * carry the most weight in the prompt.
 *
 * @param {string} characterId
 * @param {string} userId
 * @param {number} limit
 * @returns {Promise<Array<{feedback: string, created_at: string}>>}
 */
// Audit 2026-08-30 #21/#30/#39: every regen-with-feedback path records the
// correction durably. Fire-and-forget — a failed write never blocks a reply.
export async function recordFeedback(characterId, userId, feedback, surface) {
  const fb = String(feedback || '').trim();
  if (!characterId || !userId || !fb) return;
  try {
    const { error } = await supabase.from('character_feedback')
      .insert({ character_id: characterId, user_id: userId, feedback: fb.slice(0, 2000), surface: surface || 'unknown' });
    if (error) console.warn('[recordFeedback] insert failed:', error.message);
  } catch (e) { console.warn('[recordFeedback] error:', e); }
}

export async function getPriorFeedback(characterId, userId, limit = 10) {
  if (!characterId || !userId) return [];
  try {
    const { data } = await supabase
      .from('dm_messages')
      .select('feedback_context, created_at, dm_conversations!inner(character_id, user_id)')
      .not('feedback_context', 'is', null)
      .gt('feedback_context', '')
      .eq('dm_conversations.character_id', characterId)
      .eq('dm_conversations.user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);
    // The unified ledger (channel/story/group/DM since 2026-08-30) unions
    // with the legacy DM column; newest wins, capped at 18 (one pair already
    // had 46 corrections — 36 standing rules were silently expiring at 10).
    let unified = [];
    try {
      const { data: fb } = await supabase.from('character_feedback')
        .select('feedback, created_at')
        .eq('character_id', characterId)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);
      unified = fb || [];
    } catch { /* additive */ }
    const merged = [
      ...unified,
      ...(data || []).map(m => ({ feedback: m.feedback_context, created_at: m.created_at })),
    ]
      .filter(m => m.feedback && String(m.feedback).trim())
      .sort((a2, b2) => new Date(b2.created_at) - new Date(a2.created_at));
    const seen = new Set();
    return merged.filter(m => {
      const k = String(m.feedback).trim().toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    }).slice(0, Math.max(limit, 18));
  } catch (err) {
    console.error('[getPriorFeedback] error:', err);
    return [];
  }
}

/**
 * Format prior corrections into a system-prompt "DON'T" section.
 */
export function formatPriorFeedbackForPrompt(items, characterName) {
  if (!items || items.length === 0) return '';
  const lines = items.map((it, i) => {
    const when = new Date(it.created_at).toISOString().slice(0, 10);
    return `${i + 1}. [${when}] ${String(it.feedback).slice(0, 500)}`;
  });
  return `\n═══ PRIOR PLAYER CORRECTIONS — what ${characterName} has been told NOT to do (or to do differently) ═══\nThe user has corrected past responses with the notes below. Carry these forward as ongoing direction — do NOT repeat patterns that triggered correction. Treat each as a standing rule unless a later note overrides it.\n\n${lines.join('\n')}\n`;
}

/**
 * Format a list of recent character messages into a system-prompt section
 * the model can consume. Returns empty string when there are none.
 */
export function formatRecentMessagesForPrompt(recent, characterName) {
  if (!recent || recent.length === 0) return '';
  const lines = recent.map((m, i) => {
    const when = new Date(m.created_at).toISOString().slice(0, 16).replace('T', ' ');
    return `${i + 1}. [${when} · ${m.source}] ${String(m.content).slice(0, 600)}`;
  });
  return `\n═══ RECENT VOICE — last ${recent.length} things ${characterName} actually said (most recent first) ═══\nThese are verbatim previous messages from this character in THIS thread or room.\n\nHOW TO USE THEM — this distinction matters:\n• USE them for VOICE: how this character talks, their rhythm, vocabulary, humour, warmth, pet names, and above all HOW THEY SPEAK TO THESE SPECIFIC PLAYERS. Your relationship with the players and the way you address them carries across everything — never lose that.\n• Do NOT treat them as events in THIS scene. They may come from a different thread, a different route, even a different version of the story. A kiss, a confession, an injury or a relationship status that appears here did NOT necessarily happen in the conversation you are in right now.\n• THIS thread's own history (the messages in this conversation, plus its STORY SO FAR summary) is the ONLY source of truth for what has actually happened here. If these samples conflict with it, this thread wins, every time.\n• Never announce or act on an event from these samples as though the player just lived it with you here.\n\n${lines.join('\n')}\n`;
}

/**
 * Retrieves universe/world context by searching Discord RP history for scenes
 * involving the specific characters in the current story. This gives the AI
 * knowledge of settings, minor characters, and established dynamics from past RPs.
 *
 * @param {Array<string>} characterNames - Names of all characters in the scene
 * @param {number} limit - Maximum number of context snippets to return (default: 12)
 * @returns {Promise<Object>} Object with { snippets: Array, channels: Array }
 */
export async function getSceneUniverseContext(characterNames, limit = 12) {
  if (!characterNames || characterNames.length === 0) return { snippets: [], channels: [] };

  try {
    // Every scene name in one indexed lookup (THE 50-SECOND TURN, above): the
    // old seven-way ILIKE OR was a 1.6 s sequential scan per turn. Names the
    // corpus has never heard of are skipped outright.
    const lookupNames = characterNames.filter(n => n && !hasNoCorpus(n));
    if (lookupNames.length === 0) return { snippets: [], channels: [] };
    const { data, error } = await supabase.rpc('rp_history_name_samples', { p_names: lookupNames, p_limit: 400 });

    if (error || !data || data.length === 0) return { snippets: [], channels: [] };

    // Find channels where multiple scene characters appear (shared storylines)
    const channelCharMap = {};
    for (const msg of data) {
      const ch = msg.channel_name || '';
      if (!channelCharMap[ch]) channelCharMap[ch] = new Set();
      for (const name of characterNames) {
        if (ch.toLowerCase().includes(name.toLowerCase()) ||
            (msg.author || '').toLowerCase().includes(name.toLowerCase())) {
          channelCharMap[ch].add(name);
        }
      }
    }

    // Prioritize channels with the most character overlap
    const rankedChannels = Object.entries(channelCharMap)
      .map(([ch, names]) => ({ channel: ch, charCount: names.size, chars: [...names] }))
      .sort((a, b) => b.charCount - a.charCount)
      .slice(0, 8);

    // Get substantial messages from the highest-overlap channels for setting/world context
    const priorityChannels = new Set(rankedChannels.map(r => r.channel));
    const contextMsgs = data.filter(msg =>
      msg.content && msg.content.length > 80 && priorityChannels.has(msg.channel_name)
    );

    // Prioritize messages that contain setting/world-building details
    // (mentions of places, descriptions, scene-setting, character introductions)
    const settingKeywords = /campus|dorm|room|house|mansion|hall|building|class|walked|entered|arrived|sat|stood|looked|town|street|kitchen|garden|throne|court|pack|territory|office|library|cafe|restaurant/i;
    const settingMsgs = contextMsgs.filter(msg => settingKeywords.test(msg.content));
    const otherMsgs = contextMsgs.filter(msg => !settingKeywords.test(msg.content));

    // Shuffle within each group for variety
    const shuffleArray = (arr) => {
      // Deterministic stride-sample (audit P3-9): the shuffle made every
      // regeneration non-reproducible. A stride over the recency-ordered
      // pool keeps the variety without the randomness.
      {
        const stride = Math.max(1, Math.floor(arr.length / Math.max(1, Math.min(arr.length, 24))));
        const strided = arr.filter((_, i) => i % stride === 0);
        arr.length = 0;
        arr.push(...strided);
      }
      return arr;
    };
    const selected = [
      ...shuffleArray(settingMsgs).slice(0, Math.ceil(limit * 0.6)),
      ...shuffleArray(otherMsgs).slice(0, Math.floor(limit * 0.4))
    ].slice(0, limit);

    const snippets = selected.map(msg => ({
      channel: msg.channel_name || 'Unknown',
      author: msg.author || 'Unknown',
      text: msg.content.substring(0, 500)
    }));

    return { snippets, channels: rankedChannels };
  } catch (err) {
    console.error('Error fetching scene universe context:', err);
    return { snippets: [], channels: [] };
  }
}

/**
 * Retrieves character LORE KNOWLEDGE from Discord — narrative excerpts from
 * channels specifically about this character. Used as "STORY CANON" context so
 * the AI knows the character's history, faction, powers, relationships, and route
 * events as established in actual written stories.
 *
 * This is separate from voice examples (getCharacterDiscordContext). Those show
 * HOW the character talks. This shows WHAT they know and WHO they are.
 *
 * @param {string} characterName - The character's name (e.g., "Tim", "James")
 * @param {number} limit - Max excerpts to return (default: 8)
 * @returns {Promise<Array>} Array of { channel, text } lore excerpts
 */
export async function getCharacterKnowledgeContext(characterName, limit = 8) {
  if (!characterName || typeof characterName !== 'string') return [];

  try {
    const safe = sanitizeForFilter(characterName);

    // Pull from channels NAMED after this character — these are concentrated lore sources.
    // Also pull from faction/group channels the character is known to be part of.
    // Fetch a large pool and select the most substantive messages.
    const { data, error } = await supabase
      .from('rp_history')
      .select('channel_name, content, author')
      .ilike('channel_name', `%${safe}%`)
      .gt('content', '')
      .limit(limit * 12); // big pool for quality filtering

    if (error || !data || data.length === 0) return [];

    // Filter for substantive narrative content — longer messages that establish
    // facts, history, relationships, or describe what a character does/knows.
    // Skip short reactions and banter.
    const narrative = data.filter(msg =>
      msg.content &&
      msg.content.length > 120 &&
      // Prefer messages that contain third-person narration or direct dialogue revealing lore
      (msg.content.includes('*') || msg.content.length > 300 ||
       /\b(elder|immortuous|power|family|brother|sister|father|mother|member|council|always|centuries|years|known|remember|history|bond|mate|soulmate|pack|coven|faction)\b/i.test(msg.content))
    );

    // Shuffle for variety across routes and scenes
    // Deterministic stride-sample (audit P3-9): the shuffle made every
    // regeneration non-reproducible. A stride over the recency-ordered
    // pool keeps the variety without the randomness.
    {
      const stride = Math.max(1, Math.floor(narrative.length / Math.max(1, Math.min(narrative.length, 24))));
      const strided = narrative.filter((_, i) => i % stride === 0);
      narrative.length = 0;
      narrative.push(...strided);
    }

    return narrative.slice(0, limit).map(msg => ({
      channel: msg.channel_name || 'unknown',
      text: msg.content.substring(0, 700),
    }));
  } catch (err) {
    console.error('[CharacterKnowledge] Error:', err);
    return [];
  }
}


/**
 * Retrieves past route/storyline history for a character from Discord RP channels.
 * Searches for channels whose names contain the character's name, pulls key moments.
 * This gives the AI knowledge of past storylines so it can reference them accurately.
 *
 * @param {string} characterName - The character name (e.g., "Cruz", "Kya")
 * @param {number} limit - Max snippets per route channel (default: 4)
 * @returns {Promise<Array>} Array of { route: channelName, snippets: [...] }
 */
export async function getCharacterRouteHistory(characterName, limit = 4) {
  if (!characterName || typeof characterName !== 'string') return [];

  try {
    // Find distinct channels that contain this character's name (= past routes)
    const { data: channels, error: chErr } = await supabase
      .from('rp_history')
      .select('channel_name')
      .ilike('channel_name', `%${characterName.toLowerCase()}%`)
      .limit(200);

    if (chErr || !channels?.length) return [];

    // Get unique channel names and count messages per channel
    const channelCounts = {};
    for (const row of channels) {
      const ch = row.channel_name;
      channelCounts[ch] = (channelCounts[ch] || 0) + 1;
    }

    // Sort by message count (most active routes first), take top 5 routes
    const topRoutes = Object.entries(channelCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name]) => name);

    if (!topRoutes.length) return [];

    const routeHistory = [];

    for (const routeName of topRoutes) {
      // Get a few key messages from each route — prioritize longer/narrative messages
      const { data: msgs } = await supabase
        .from('rp_history')
        .select('content, author')
        .eq('channel_name', routeName)
        .ilike('author', `%${characterName}%`)
        .gt('content', '')
        .order('message_timestamp', { ascending: false })
        .limit(limit * 3);

      if (!msgs?.length) continue;

      // Pick the most substantial messages
      const substantial = msgs
        .filter(m => m.content && m.content.length > 80)
        .slice(0, limit)
        .map(m => m.content.substring(0, 300));

      if (substantial.length > 0) {
        routeHistory.push({
          route: routeName,
          snippets: substantial,
        });
      }
    }

    return routeHistory;
  } catch (err) {
    console.error('Error fetching character route history:', err);
    return [];
  }
}

/**
 * getPlayerVoiceSamples — pull real samples of how the HUMAN players (Haley /
 * Nicole) actually write and talk, from their Discord history, so a character
 * can read their humor, emotion, sarcasm, and rapport instead of taking every
 * line at face value. Draws from the OOC channels (where they talk as
 * themselves) — the players explicitly OK'd referencing their OOC chats.
 *
 * @param {string[]} displayNames e.g. ['Haley','Nicole']
 * @param {number} limit samples per player
 * @returns {Promise<Object>} { haley: [".."], nicole: [".."] } keyed by writer
 */
export async function getPlayerVoiceSamples(displayNames, limit = 7) {
  const writers = [...new Set((displayNames || []).map(n => String(n || '').toLowerCase().trim()))]
    .filter(w => w === 'haley' || w === 'nicole');
  if (!writers.length) return {};
  try {
    const { data: accts } = await supabase
      .from('writer_accounts').select('account_name, writer').in('writer', writers);
    if (!accts?.length) return {};
    const acctByWriter = {};
    for (const a of accts) { (acctByWriter[a.writer] = acctByWriter[a.writer] || []).push(a.account_name); }
    // Channels where the players talk AS THEMSELVES (real voice / banter).
    const OOC_CHANNELS = ['ooc', 'ooc-gen', 'general', 'mm_chatroom', 'wifey-chat', 'secret-wifey-chat', 'chat', 'spam'];
    const out = {};
    await Promise.all(writers.map(async (w) => {
      const accounts = acctByWriter[w] || [];
      if (!accounts.length) return;
      const { data } = await supabase
        .from('rp_history')
        .select('content')
        .in('author', accounts)
        .in('channel_name', OOC_CHANNELS)
        .order('message_timestamp', { ascending: false })
        .limit(limit * 12);
      const seen = new Set();
      const cleaned = (data || [])
        .map(r => (r.content || '').replace(/\s+/g, ' ').trim())
        .filter(c => c.length >= 22 && c.length <= 280 && !/^https?:\/\//i.test(c) && !/^[<:@]/.test(c))
        .filter(c => { const k = c.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; });
      // Spread across the pool (not just the last rapid-fire exchange) so the
      // samples show range, then keep `limit`.
      const step = Math.max(1, Math.floor(cleaned.length / limit));
      const samples = [];
      for (let i = 0; i < cleaned.length && samples.length < limit; i += step) samples.push(cleaned[i]);
      if (samples.length) out[w] = samples;
    }));
    return out;
  } catch (err) {
    console.warn('[discordContext] getPlayerVoiceSamples failed:', err?.message);
    return {};
  }
}

// ─── PERCEPTION TAG (anti mind-reading) ─────────────────────────────────────
// The single most effective anti-mind-reading measure, used SERVER-WIDE (DMs,
// channels, RP stories). A weaker model can't reliably separate spoken dialogue
// from narration/interior thought in raw prose, so it answers things the player
// never said out loud ("she didn't want to be babysat" → "I don't babysit you").
// We compute it deterministically instead: pull the quoted speech out of a
// player message and state plainly what may be reacted to.
// ─── WHICH MESSAGE IS THE LIVE ONE (2026-09-23) ──────────────────────────────
// Haley: "the ai is getting confused again in DMs with the memory — it's
// responding to messages from the past again once in a while."
//
// DM prompts measured 113,000–175,000 input tokens: the thread is sent
// VERBATIM (scopeFullHistory, up to 8,000 messages / 2.4M chars) because
// verbatim memory beats summarised memory for this app, and that is not being
// undone. But across 120k tokens of alternating turns, nothing said which one
// was waiting on a reply — so the model occasionally picked a salient older
// message and answered that.
//
// dialects.ts already has the sentence for this (MEMORY_BOUNDARY: "The message
// you are answering is the last thing in this turn"), but it only ships with
// the dynamic-tail layout, and that layout was reverted after a live quality
// regression (A2). So this restores the GUARANTEE without the layout: one
// bracketed note on the final user turn, in the same shape as the PERCEPTION
// tag that already rides every user message without leaking.
//
// Permission-side, per the standing rule: it says what the model MAY do with
// the history as well as what it must not do to it.
export const CURRENT_TURN_MARKER = '\n(NOW — THE LIVE MESSAGE: THIS message is the newest thing said and the only one waiting on a reply. Every earlier message in this conversation is history you both already lived: remember it, draw on it, refer back to it freely — but none of it is unanswered and none of it just arrived. Answer THIS message. Never reply to an earlier one as though it were new.)';

/**
 * Mark the final user turn as the live one. No-op unless the last entry is a
 * user message, so it can never disturb role alternation.
 * @param {Array<{role:string,content:string}>} msgs
 */
export function markCurrentTurn(msgs) {
  // Mutates in place and returns the same array: callers append the text-mode
  // directive and the director block to this list afterwards, so handing back
  // a copy would silently drop the marker.
  if (!Array.isArray(msgs)) return msgs;
  const i = msgs.length - 1;
  if (i < 0 || msgs[i]?.role !== 'user' || typeof msgs[i]?.content !== 'string') return msgs;
  if (msgs[i].content.includes('(NOW — THE LIVE MESSAGE:')) return msgs; // idempotent
  msgs[i] = { ...msgs[i], content: msgs[i].content + CURRENT_TURN_MARKER };
  return msgs;
}

export function perceptionTag(text) {
  // Audibility-aware: a quote wrapped in "softly / under her breath / whispered
  // / to herself"-type cues was deliberately said BELOW hearing — other
  // characters must not overhear it. (Treating every quote as spoken-aloud made
  // characters supernaturally hear whispers the player explicitly made quiet.)
  const { heard, unheard } = splitAudibility(text);
  const unheardNote = unheard.length
    ? ` These words were said TOO QUIETLY to be heard by anyone else: ${unheard.join(' ')} — other characters did NOT hear them (at most a murmur, lips moving).`
    : '';
  if (heard.length) {
    return `\n(PERCEPTION: the only words SPOKEN ALOUD above are ${heard.join(' ')} — everything else is narration or private thought. React to the spoken words and to any physical action described; do NOT react to, name, or answer anything the character only thought or felt.${unheardNote})`;
  }
  return `\n(PERCEPTION: NOTHING audible was spoken above — it is narration, interior thought${unheard.length ? ', or words said too quietly to carry' : ''}. React ONLY to the physical actions actually described. You cannot perceive what the character is thinking, wanting, worrying about, or deciding, and must not reference it.${unheardNote})`;
}

// The matching rule text. Placed LAST in a prompt (recency) for weak models.
/**
 * Strip any (PERCEPTION: ...) note the model echoed back into its own reply.
 *
 * The note is scaffolding written FOR the model about what its character can
 * perceive; it must never appear in a message a player reads. It leaked live
 * on 2026-08-01 because a co-player's messages were labelled as assistant
 * turns with the note attached, so the model saw the format inside its own
 * voice and reproduced it. That labelling is fixed at the source, but this is
 * the belt: a leak reaching the transcript is permanent, and cheap to prevent.
 */
export function stripPerceptionTag(text) {
  if (!text) return text;
  return String(text)
    .replace(/\(\s*PERCEPTION:[\s\S]*?\)\s*/gi, '')
    .replace(/[ \t]+$/gm, '')
    .trim();
}

export const PERCEPTION_RULES = `
═══ LAST AND MOST IMPORTANT — YOU CANNOT READ MINDS ═══

Each player message is followed by a (PERCEPTION: ...) note computed for you. It tells you exactly what was SPOKEN ALOUD. Obey it literally.
- React ONLY to spoken words and to physical actions that were actually described (biting a lip, scrubbing a plate faster, stepping back).
- A character's thoughts, wants, worries, embarrassment, reasons and decisions are INVISIBLE to you unless they said them out loud. Do not answer them, do not name them, do not reassure them about them, and do not echo the specific words the player used only in narration.
- WRONG: the player narrates "she didn't want to be babysat" and you reply "I don't babysit you." You never heard that word. That single move breaks the scene harder than anything else you can do.
- RIGHT: you noticed she went quiet and started scrubbing faster, so you respond to THAT — the visible behavior — without naming the reason behind it.
- You may read body language and make a WRONG guess. Being a little wrong is good writing. Being telepathically right is not.
- The narration and thoughts ARE for you as the WRITER: read them as the player steering the scene — mood, pacing, what they're hoping for — and let that shape your reply. The line is simple: use them to decide HOW to write; never let the character KNOW what they contain.
- The ONLY exception: if your own identity above explicitly says you read minds or sense emotions, use it as your canon defines. Otherwise you cannot, ever.`;
